import { useCallback } from 'react'
import {
  useAnimatedStyle,
  useReducedMotion,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated'

import { useHapticFeedback } from '../haptics/HapticsContext'
import { MOTION_EASING } from '../utils/motion'

// Shallow enough to read as the control yielding rather than shrinking, and
// short enough to land before the finger lifts.
const PRESSED_SCALE = 0.97
const PRESS_TIMING = { duration: 120, easing: MOTION_EASING }

/**
 * A row or a disclosure gives a little; the action a deposit turns on gives
 * more, because it is the one press a buyer needs to feel they have made.
 */
export const ACTION_PRESSED_SCALE = 0.95
export const ACTION_PRESSED_DIM = 0.85

export interface PressScaleOptions {
  /** Scale at the bottom of the press. */
  scale?: number
  /** Opacity at the bottom of the press; 1 leaves the control at full strength. */
  dim?: number
  /** Fire a tap as the finger lands. Off for controls that are not an action. */
  haptics?: boolean
}

/**
 * Press feedback for a control the design gives no pressed fill: it dips under
 * the finger on press-in and springs back on press-out. Scale takes the label
 * and any icon with it, which is what makes it read as one physical object.
 *
 * Dip and dim run off one value, so the control is at its darkest exactly at
 * the bottom of the travel rather than drifting against it.
 *
 * Reduced motion drops the dip but keeps the dim, applied without a ramp: the
 * press still has to answer, and a control that changes strength in place asks
 * nothing of a vestibular system. The tap is unaffected — it is not motion.
 */
export function usePressScale({
  scale = PRESSED_SCALE,
  dim = 1,
  haptics = false,
}: PressScaleOptions = {}) {
  const reduceMotion = useReducedMotion()
  const hapticFeedback = useHapticFeedback()
  // 0 at rest, 1 at the bottom of the press.
  const pressed = useSharedValue(0)

  const pressStyle = useAnimatedStyle(() => {
    // Spelled out because the plugin workletizes by the hook's name, and esbuild
    // renames this one in the bundle (see AmountDisplay).
    'worklet'
    const depth = pressed.value
    return {
      transform: reduceMotion ? [] : [{ scale: 1 - depth * (1 - scale) }],
      opacity: 1 - depth * (1 - dim),
    }
  })

  const onPressIn = useCallback(() => {
    if (haptics) {
      hapticFeedback('tap')
    }
    pressed.value = reduceMotion ? 1 : withTiming(1, PRESS_TIMING)
  }, [pressed, reduceMotion, haptics, hapticFeedback])

  const onPressOut = useCallback(() => {
    pressed.value = reduceMotion ? 0 : withTiming(0, PRESS_TIMING)
  }, [pressed, reduceMotion])

  return { pressStyle, onPressIn, onPressOut }
}
